import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { AiOutlinePlus, AiOutlineMinus, AiOutlineExpand, AiOutlineSelect } from 'react-icons/ai';
import './ZoomableImage.css';

export const MIN_SCALE = 1;
export const MAX_SCALE = 12;

// How far a pointer may travel and still count as a click rather than a drag.
const CLICK_SLOP_PX = 6;
// Gap between a release and the next press that still reads as a double-tap.
const DOUBLE_TAP_MS = 320;
// Vertical pixels of drag per e-fold of zoom. Lower = more zoom per pixel.
const DRAG_ZOOM_PX_PER_EFOLD = 180;
// A selection smaller than this is a mis-click, not an intentional region.
const MIN_BOX_PX = 12;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * The photo surface in both lightboxes: wheel/pinch/double-tap zoom from
 * react-zoom-pan-pinch, plus two drag gestures it doesn't provide.
 *
 *   Drag a box   - desktop. Hold Shift (or arm the ⬚ button) and drag a rectangle;
 *                  the view zooms to exactly that region. Precise for one detail.
 *   Drag up/down - touch and mouse. Double-tap and *hold*, then drag vertically to
 *                  zoom smoothly around the point under your finger. One-handed, and
 *                  it leaves ordinary one-finger panning alone because it only arms
 *                  after a double-tap.
 *
 * Both are layered over the library rather than replacing it, so pinch and wheel
 * keep working exactly as before.
 */
export default function ZoomableImage({
  src,
  alt = '',
  rotation = 0,
  /** Changing this resets zoom/pan — pass image id + rotation. */
  resetKey,
  /** Called for a click on empty space (not a drag, not on the photo itself). */
  onBackdropClick,
  className = '',
}) {
  const apiRef = useRef(null);
  const surfaceRef = useRef(null);
  const imgRef = useRef(null);

  const [areaArmed, setAreaArmed] = useState(false);
  // The stage's own size, measured rather than assumed. A quarter-turned photo is
  // bounded by the *opposite* axis, and CSS can't express "the other side of my
  // container" without container queries, which aren't universal yet.
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const [box, setBox] = useState(null); // {x, y, w, h} in surface-local px

  // Everything about the in-flight gesture. A ref, not state: these update on every
  // pointermove and re-rendering at that rate would make the drag feel heavy.
  const g = useRef({
    mode: null, // 'box' | 'dragZoom' | null
    startX: 0, startY: 0,
    startScale: 1,
    anchorX: 0, anchorY: 0,
    moved: 0,
    lastTapAt: 0, lastTapX: 0, lastTapY: 0,
    pointerId: null,
  });

  useEffect(() => {
    const el = surfaceRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const r = entry.contentRect;
      setStage({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /**
   * Re-fit once the photo actually has a size.
   *
   * react-zoom-pan-pinch measures its content when it mounts. A photo that hasn't
   * downloaded yet measures 0x0, so the transform it computes is for nothing — and
   * when the bytes arrive the image is laid out against that stale transform,
   * landing hard against the top-left and overflowing the stage. Resetting on load
   * recomputes it against the real dimensions.
   *
   * Also covers a cached image that fires load before the ref is wired up, and a
   * browser that re-decodes on a src change.
   */
  const handleImageLoad = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    // Next frame: let layout settle with the image's real size first.
    requestAnimationFrame(() => {
      try {
        api.resetTransform(0);
        api.centerView?.(1, 0);
      } catch {
        /* the viewer is usable without a perfect fit; never throw here */
      }
    });
  }, []);

  const quarterTurned = Math.abs(rotation % 180) === 90;

  /**
   * Hard pixel bounds, not percentages.
   *
   * `max-height: 100%` has to resolve through the zoom library's own wrapper and
   * content elements, which it sizes inline and re-sizes as it transforms. When
   * that chain doesn't give a definite height the image is bounded by width only,
   * so a tall photo renders at full height, overflows the stage and clips against
   * the top-left. Measuring the stage ourselves and setting px caps on the <img>
   * sidesteps the whole chain: an inline style on the image beats everything.
   *
   * A quarter turn is applied after layout, so its bounds are swapped.
   */
  const imgLimits = stage.w && stage.h
    ? (quarterTurned
        ? { maxWidth: `${stage.h}px`, maxHeight: `${stage.w}px` }
        : { maxWidth: `${stage.w}px`, maxHeight: `${stage.h}px` })
    : undefined;

  const surfaceRect = () => surfaceRef.current?.getBoundingClientRect();

  /**
   * Is this point on the photo itself?
   *
   * Tested geometrically rather than with `event.target`, because the zoom library
   * calls setPointerCapture on its own wrapper while panning: pointerup then
   * retargets off the <img>, and a target-based check would report "empty space"
   * for a click squarely on the photo - closing the viewer under the user.
   */
  const isOnPhoto = (clientX, clientY) => {
    const r = imgRef.current?.getBoundingClientRect();
    if (!r) return false;
    return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  };

  /**
   * Current scale/offset. The ref exposes this as `state`; older builds of the
   * library put it on `instance.transformState`. Read whichever is present and
   * return null rather than throwing if the ref isn't wired up yet — a gesture
   * that can't read the transform should do nothing, not crash the viewer.
   */
  const readTransform = () => {
    const api = apiRef.current;
    const t = api?.state ?? api?.instance?.transformState;
    if (!t || typeof t.scale !== 'number') return null;
    return { scale: t.scale, positionX: t.positionX ?? 0, positionY: t.positionY ?? 0 };
  };

  const handlePointerDown = useCallback(
    (e) => {
      if (e.button != null && e.button !== 0) return;
      const now = Date.now();
      const st = g.current;
      st.moved = 0;
      st.startX = e.clientX;
      st.startY = e.clientY;
      st.anchorX = e.clientX;
      st.anchorY = e.clientY;
      st.pointerId = e.pointerId;
      st.startScale = readTransform()?.scale ?? 1;

      const isDoubleTapHold =
        now - st.lastTapAt < DOUBLE_TAP_MS &&
        Math.hypot(e.clientX - st.lastTapX, e.clientY - st.lastTapY) < 24;

      if (areaArmed || e.shiftKey) {
        st.mode = 'box';
        const r = surfaceRect();
        if (r) setBox({ x: e.clientX - r.left, y: e.clientY - r.top, w: 0, h: 0 });
        e.preventDefault();
      } else if (isDoubleTapHold) {
        st.mode = 'dragZoom';
        e.preventDefault();
      } else {
        st.mode = null; // leave it to the library: pan, pinch, double-click
      }

      if (st.mode) e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [areaArmed]
  );

  const handlePointerMove = useCallback((e) => {
    const st = g.current;
    st.moved = Math.max(st.moved, Math.hypot(e.clientX - st.startX, e.clientY - st.startY));
    if (!st.mode) return;

    if (st.mode === 'box') {
      const r = surfaceRect();
      if (!r) return;
      const x0 = st.startX - r.left;
      const y0 = st.startY - r.top;
      const x1 = clamp(e.clientX - r.left, 0, r.width);
      const y1 = clamp(e.clientY - r.top, 0, r.height);
      setBox({ x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) });
      return;
    }

    // dragZoom — upward drag magnifies, anchored on where the gesture began so the
    // detail under the finger stays put instead of sliding away.
    const api = apiRef.current;
    if (!api) return;
    const dy = st.startY - e.clientY;
    const next = clamp(st.startScale * Math.exp(dy / DRAG_ZOOM_PX_PER_EFOLD), MIN_SCALE, MAX_SCALE);
    api.zoomToPoint(next, st.anchorX, st.anchorY, 0);
  }, []);

  const applyBoxZoom = useCallback((rect) => {
    const api = apiRef.current;
    const r = surfaceRect();
    const t = readTransform();
    if (!api || !r || !t || rect.w < MIN_BOX_PX || rect.h < MIN_BOX_PX) return;

    const { scale, positionX, positionY } = t;
    // Selection -> content space, then pick the scale that fits it to the surface.
    const next = clamp(scale * Math.min(r.width / rect.w, r.height / rect.h), MIN_SCALE, MAX_SCALE);
    const centreContentX = (rect.x - positionX) / scale + rect.w / (2 * scale);
    const centreContentY = (rect.y - positionY) / scale + rect.h / (2 * scale);
    api.setTransform(
      r.width / 2 - next * centreContentX,
      r.height / 2 - next * centreContentY,
      next,
      220
    );
  }, []);

  const handlePointerUp = useCallback(
    (e) => {
      const st = g.current;
      const mode = st.mode;
      st.mode = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);

      if (mode === 'box') {
        if (box) applyBoxZoom(box);
        setBox(null);
        setAreaArmed(false);
        st.lastTapAt = 0;
        return;
      }

      if (mode === 'dragZoom') {
        st.lastTapAt = 0;
        return;
      }

      st.lastTapAt = Date.now();
      st.lastTapX = e.clientX;
      st.lastTapY = e.clientY;

      // A genuine click on empty space closes. A drag never does, so panning a
      // zoomed photo to the edge can't dismiss it by accident.
      if (st.moved < CLICK_SLOP_PX && !isOnPhoto(e.clientX, e.clientY)) onBackdropClick?.();
    },
    [box, applyBoxZoom, onBackdropClick]
  );

  const gestureActive = g.current.mode !== null;

  return (
    <TransformWrapper
      key={resetKey}
      ref={apiRef}
      initialScale={1}
      minScale={MIN_SCALE}
      maxScale={MAX_SCALE}
      doubleClick={{ mode: 'toggle', step: 3 }}
      wheel={{ step: 0.22 }}
      pinch={{ step: 6 }}
      // Panning is suspended only while one of our own drags is running.
      panning={{ disabled: areaArmed, velocityDisabled: true }}
      centerOnInit
    >
      {({ zoomIn, zoomOut, resetTransform }) => (
        /* A column: the photo takes the space that's left, the controls sit below
           it. They used to be absolutely positioned over the stage, which was
           invisible on a portrait photo — the pill covered the bottom of the
           image, and that is the "vertical photos get cut" report. */
        <div className="zi-root">
          <div
            ref={surfaceRef}
            className={`zi-stage ${areaArmed ? 'zi-stage--area' : ''} ${className}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={() => { g.current.mode = null; setBox(null); }}
          >
            <TransformComponent wrapperClass="zi-wrapper" contentClass="zi-content">
              <img
                ref={imgRef}
                src={src}
                alt={alt}
                className="zi-img"
                style={{
                  ...(rotation ? { transform: `rotate(${rotation}deg)` } : null),
                  ...imgLimits,
                }}
                draggable={false}
                onLoad={handleImageLoad}
              />
            </TransformComponent>

            {box && box.w > 0 && (
              <div
                className="zi-box"
                style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
              />
            )}
          </div>

          <div className="zi-controls" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => zoomOut()} title="Zoom out" aria-label="Zoom out">
              <AiOutlineMinus size={16} />
            </button>
            <button type="button" onClick={() => zoomIn()} title="Zoom in" aria-label="Zoom in">
              <AiOutlinePlus size={16} />
            </button>
            <button
              type="button"
              className={areaArmed ? 'zi-btn--on' : ''}
              onClick={() => setAreaArmed((v) => !v)}
              title="Zoom to an area — or hold Shift and drag"
              aria-label="Zoom to an area"
              aria-pressed={areaArmed}
            >
              <AiOutlineSelect size={16} />
            </button>
            <button
              type="button"
              onClick={() => { setAreaArmed(false); resetTransform(); }}
              title="Fit to screen"
              aria-label="Fit to screen"
            >
              <AiOutlineExpand size={16} />
            </button>
          </div>

          {areaArmed && !gestureActive && (
            <div className="zi-hint">Drag a box over the area to magnify</div>
          )}
        </div>
      )}
    </TransformWrapper>
  );
}
